import { useQuery } from '@tanstack/react-query';
import type { BranchDto, ProfitLossStatement, ReportExportRequest } from '@sellokart/shared';
import { PageHeader } from '@/components/PageHeader';
import { AsyncSection } from '@/components/AsyncSection';
import { KpiCard } from '@/components/KpiCard';
import { ExportMenu } from '@/components/ExportMenu';
import { ReportFilterBar } from '@/components/ReportFilterBar';
import { ReportPeriodButton, ReportToolbar } from '@/components/ReportToolbar';
import { TableSkeleton } from '@/components/Table';
import { fetchProfitLoss, profitLossKeys } from '@/features/reports/profit-loss-api';
import { downloadReportExport } from '@/features/reports/api';
import type { ExportFormat } from '@/features/reports/api';
import { buildExportTable, exportSubtitle } from '@/features/reports/export-table';
import { useReportFilters } from '@/features/reports/useReportFilters';
import { useReportToolbar } from '@/features/reports/useReportToolbar';
import { usePortal } from '@/features/portal/PortalContext';

const money = (value: number) => value.toFixed(2);
const percent = (value: number | null) => (value === null ? '—' : `${value.toFixed(1)}%`);

type LineStyle = 'row' | 'indent' | 'sub' | 'total' | 'grand';

interface StatementLine {
  label: string;
  amount: number;
  style: LineStyle;
}

/** The POS's Statement tab, top to bottom — a line only appears when it moved, for the optional ones. */
function statementLines(st: ProfitLossStatement): StatementLine[] {
  const moved = (v: number) => Math.abs(v) > 0.004;
  return [
    { label: 'Sales Revenue', amount: st.salesRevenue, style: 'row' },
    { label: 'Sales Returns (−)', amount: st.salesReturns, style: 'row' },
    ...(moved(st.creditNotes)
      ? [{ label: 'Credit Notes (−)', amount: st.creditNotes, style: 'row' as const }]
      : []),
    { label: 'Net Sales', amount: st.netSales, style: 'sub' },
    { label: 'Cost of Goods Sold (−)', amount: st.costOfGoodsSold, style: 'row' },
    { label: 'GROSS PROFIT', amount: st.grossProfit, style: 'total' },
    { label: 'Discounts Given (−)', amount: st.discounts, style: 'row' },
    { label: 'Balances Waived (−)', amount: st.waived, style: 'row' },
    ...(moved(st.debitNotes)
      ? [{ label: 'Debit Notes — cost claimed back (+)', amount: st.debitNotes, style: 'row' as const }]
      : []),
    { label: 'Expenses (−)', amount: st.totalExpenses, style: 'row' },
    ...st.expenses.map((e) => ({ label: e.label, amount: e.amount, style: 'indent' as const })),
    ...(moved(st.totalStockIssued)
      ? [
          { label: 'Stock Issued — used or lost (−)', amount: st.totalStockIssued, style: 'row' as const },
          ...st.stockIssued.map((e) => ({ label: e.label, amount: e.amount, style: 'indent' as const })),
        ]
      : []),
    { label: st.netProfit < -0.004 ? 'NET LOSS' : 'NET PROFIT', amount: st.netProfit, style: 'grand' },
  ];
}

const COST_NOTE =
  "Sales are valued before the bill discount, excl. VAT; returns at the value of the line they reverse. " +
  "Voided sales and opening balances are left out. Cost of goods sold uses each lot's (else product's) " +
  'current cost, as the per-sale cost frozen on the till is not synced.';

/** The statement as shown — a breakdown line indented under its heading — with the margins as notes. */
function profitLossTable(
  statement: ProfitLossStatement,
  branch: { branchId: string; branches: readonly BranchDto[] },
): ReportExportRequest {
  return buildExportTable({
    title: 'Profit & loss',
    subtitle: exportSubtitle(statement, branch),
    from: statement.from,
    to: statement.to,
    columns: [
      { key: 'label', header: 'Particulars', width: 3, text: (line) => (line.style === 'indent' ? `    ${line.label}` : line.label) },
      { key: 'amount', header: 'Amount', align: 'right', money: true, text: (line) => money(line.amount) },
    ],
    rows: statementLines(statement),
    notes: [
      `Gross margin: ${percent(statement.grossMargin)} · Net margin: ${percent(statement.netMargin)}`,
      COST_NOTE,
      ...(statement.linesWithoutCost > 0
        ? [`${statement.linesWithoutCost} sold line(s) have no cost recorded, so gross profit is overstated by what those items cost.`]
        : []),
    ],
  });
}

function StatementTable({ statement }: { statement: ProfitLossStatement }) {
  const lines = statementLines(statement);
  return (
    <div className="max-w-3xl overflow-hidden rounded-lg border border-slate-200 bg-white">
      <table className="w-full text-sm">
        <caption className="sr-only">Profit and loss statement</caption>
        <thead>
          <tr className="border-b border-slate-200 text-left text-xs font-medium uppercase tracking-wide text-slate-500">
            <th scope="col" className="px-4 py-3">Particulars</th>
            <th scope="col" className="px-4 py-3 text-right">Amount</th>
          </tr>
        </thead>
        <tbody>
          {lines.map((line, index) => {
            const big = line.style === 'total' || line.style === 'grand';
            const bold = big || line.style === 'sub';
            const tone = big ? (line.amount < -0.004 ? 'text-red-700' : 'text-green-700') : 'text-slate-900';
            const background = line.style === 'grand' ? 'bg-slate-200' : line.style === 'total' ? 'bg-slate-100' : '';
            return (
              <tr key={`${line.label}-${index}`} className={`border-b border-slate-100 last:border-0 ${background}`}>
                <th
                  scope="row"
                  className={`px-4 ${big ? 'py-3' : 'py-2'} text-left ${
                    line.style === 'indent' ? 'pl-10 font-normal text-slate-500' : bold ? 'font-semibold text-slate-900' : 'font-medium text-slate-700'
                  }`}
                >
                  {line.label}
                </th>
                <td
                  className={`px-4 ${big ? 'py-3 text-base' : 'py-2'} text-right tabular-nums ${
                    line.style === 'indent' ? 'text-slate-500' : tone
                  } ${bold ? 'font-semibold' : ''}`}
                >
                  {money(line.amount)}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

export function ProfitLossPage() {
  const { profile } = usePortal();
  const [filters, setFilters] = useReportFilters();
  const toolbar = useReportToolbar(filters, setFilters);

  const query = useQuery({
    queryKey: profitLossKeys.statement(filters),
    queryFn: ({ signal }) => fetchProfitLoss(filters, signal),
  });
  const loaded = query.data;

  const onExport = (format: ExportFormat) =>
    loaded
      ? downloadReportExport(
          profitLossTable(loaded, { branchId: filters.branchId, branches: profile?.branches ?? [] }),
          format,
        )
      : Promise.resolve();

  return (
    <div>
      <PageHeader
        title="Profit & loss"
        description="Net sales, cost of goods sold, gross profit and expenses for the selected window — all excl. VAT."
      />
      <ReportFilterBar filters={filters} onChange={setFilters} branches={profile?.branches ?? []} hideDateRange />
      <ReportToolbar>
        <ReportPeriodButton from={filters.from} to={filters.to} onChange={toolbar.setRange} />
        {loaded ? (
          <div className="ml-auto">
            <ExportMenu onExport={onExport} />
          </div>
        ) : null}
      </ReportToolbar>
      <AsyncSection
        state={query}
        isEmpty={() => false}
        skeleton={<TableSkeleton rows={10} columns={2} />}
      >
        {(statement: ProfitLossStatement) => (
          <div className="space-y-6">
            <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
              <KpiCard label="Net sales" value={money(statement.netSales)} />
              <KpiCard label="Gross profit" value={money(statement.grossProfit)} />
              <KpiCard label="Gross margin" value={percent(statement.grossMargin)} />
              <KpiCard
                label={statement.netProfit < -0.004 ? 'Net loss' : 'Net profit'}
                value={`${money(statement.netProfit)} (${percent(statement.netMargin)})`}
              />
            </div>
            <StatementTable statement={statement} />
            <p className="max-w-3xl text-xs text-slate-500">
              Sales are valued before the bill discount, excl. VAT; returns at the value of the line they
              reverse. Voided sales and opening balances are left out. Cost of goods sold uses each lot's
              (else product's) current cost, as the per-sale cost frozen on the till is not synced.
            </p>
            {statement.linesWithoutCost > 0 ? (
              <p className="max-w-3xl rounded-md bg-orange-50 px-3 py-2 text-sm text-orange-800">
                {statement.linesWithoutCost} sold line{statement.linesWithoutCost === 1 ? ' has' : 's have'} no cost
                recorded, so gross profit is overstated by whatever those items cost. Enter the product cost on the
                till to correct it.
              </p>
            ) : null}
          </div>
        )}
      </AsyncSection>
    </div>
  );
}
